import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const EMPTY_COUPON_FORM = {
  code: '',
  discountType: 'percent',
  discountValue: '',
  minOrderAmount: '',
  maxUses: '',
  expiresAt: '',
};

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXMark /> : <IconInfo />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const CouponPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form, setForm] = useState(EMPTY_COUPON_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // Confirm
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchCoupons = useCallback(async () => {
    if (!storeId) {
      setCoupons([]);
      setIsLoading(false);
      return;
    }
    try {
      const res = await api.get(`/admin/coupons/${storeId}`);
      const data = res.data;
      setCoupons(Array.isArray(data) ? data : data.coupons || []);
    } catch {
      setCoupons([]);
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const openCreate = () => {
    setEditingCoupon(null);
    setForm(EMPTY_COUPON_FORM);
    setShowModal(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code || '',
      discountType: coupon.discountType || coupon.discount_type || 'percent',
      discountValue: coupon.discountValue ?? coupon.discount_value ?? '',
      minOrderAmount: coupon.minOrderAmount ?? coupon.min_order_amount ?? '',
      maxUses: coupon.maxUses ?? coupon.max_uses ?? '',
      expiresAt: coupon.expiresAt || coupon.expires_at ? (coupon.expiresAt || coupon.expires_at).slice(0, 10) : '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { addToast('쿠폰 코드를 입력해주세요.', 'error'); return; }
    if (!form.discountValue || isNaN(Number(form.discountValue))) { addToast('할인 값을 올바르게 입력해주세요.', 'error'); return; }

    setIsSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discountType,
      discount_value: Number(form.discountValue),
      min_order_amount: Number(form.minOrderAmount) || 0,
      max_uses: Number(form.maxUses) || 0,
      expires_at: form.expiresAt ? new Date(form.expiresAt + 'T23:59:59').toISOString() : null,
    };

    try {
      if (editingCoupon) {
        const id = editingCoupon._id || editingCoupon.id;
        if (storeId) {
          const res = await api.put(`/admin/coupons/${id}`, payload);
          const updated = res.data?.coupon || res.data || { ...editingCoupon, ...payload };
          setCoupons((prev) => prev.map((c) => (c._id || c.id) === id ? { ...c, ...updated } : c));
        } else {
          setCoupons((prev) => prev.map((c) => (c._id || c.id) === id ? { ...c, ...payload } : c));
        }
        addToast('쿠폰이 수정되었습니다.', 'success');
      } else {
        if (storeId) {
          const res = await api.post(`/admin/coupons/${storeId}`, payload);
          const created = res.data?.coupon || res.data || { _id: `cp-${Date.now()}`, ...payload, usedCount: 0, isActive: true };
          setCoupons((prev) => [...prev, created]);
        } else {
          setCoupons((prev) => [...prev, { _id: `cp-${Date.now()}`, ...payload, usedCount: 0, isActive: true }]);
        }
        addToast('쿠폰이 생성되었습니다.', 'success');
      }
      setShowModal(false);
    } catch (err) {
      addToast(err.response?.data?.message || '저장에 실패했습니다.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (coupon) => {
    setConfirmDialog({
      title: '쿠폰 삭제',
      message: `"${coupon.code}" 쿠폰을 삭제하시겠습니까?`,
      danger: true,
      onConfirm: async () => {
        const id = coupon._id || coupon.id;
        try {
          if (storeId) await api.delete(`/admin/coupons/${id}`);
          setCoupons((prev) => prev.filter((c) => (c._id || c.id) !== id));
          addToast('쿠폰이 삭제되었습니다.', 'success');
        } catch { addToast('삭제에 실패했습니다.', 'error'); }
        setConfirmDialog(null);
      },
    });
  };

  const handleToggleActive = async (coupon) => {
    const id = coupon._id || coupon.id;
    const newActive = !(coupon.isActive ?? coupon.is_active ?? true);
    setCoupons((prev) => prev.map((c) => (c._id || c.id) === id ? { ...c, isActive: newActive, is_active: newActive } : c));
    try {
      if (storeId) {
        await api.put(`/admin/coupons/${id}`, { is_active: newActive });
      }
      addToast(newActive ? '쿠폰이 활성화되었습니다.' : '쿠폰이 비활성화되었습니다.', 'success');
    } catch {
      setCoupons((prev) => prev.map((c) => (c._id || c.id) === id ? { ...c, isActive: !newActive, is_active: !newActive } : c));
      addToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {[0,1,2].map((i) => (
            <div key={i} className="skeleton-card animate-fade-in-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div className="skeleton-line w-60 h-24" />
                <div className="skeleton-line w-40" style={{ height: 10 }} />
              </div>
              <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="skeleton-line w-80" />
                  <div className="skeleton-line w-60" />
                  <div className="skeleton-line w-60" />
                  <div className="skeleton-line w-80" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">쿠폰 관리</h1>
          <p className="page-subtitle">쿠폰 및 할인을 관리합니다 · 총 {coupons.length}개</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          + 쿠폰 생성
        </button>
      </div>

      {/* Coupons list */}
      {coupons.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
            </div>
            <h3>쿠폰이 없습니다</h3>
            <p>새 쿠폰을 생성하여 고객에게 할인을 제공하세요.</p>
            <button className="btn btn-primary mt-16" onClick={openCreate}>
              + 쿠폰 생성
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {coupons.map((coupon, idx) => {
            const id = coupon._id || coupon.id;
            const isActive = coupon.isActive ?? coupon.is_active ?? true;
            const discountType = coupon.discountType || coupon.discount_type || 'percent';
            const discountValue = coupon.discountValue ?? coupon.discount_value ?? 0;
            const usedCount = coupon.usedCount ?? coupon.used_count ?? 0;
            const maxUses = coupon.maxUses ?? coupon.max_uses ?? 0;
            const minOrder = coupon.minOrderAmount ?? coupon.min_order_amount ?? 0;
            const expiresAt = coupon.expiresAt || coupon.expires_at;
            const isExpired = expiresAt && new Date(expiresAt) < new Date();

            return (
              <div
                key={id}
                className="animate-fade-in-up"
                style={{
                  background: 'var(--surface)',
                  borderRadius: 'var(--radius-lg)',
                  border: `1.5px solid ${isActive && !isExpired ? 'var(--primary)' : 'var(--border)'}`,
                  boxShadow: 'var(--shadow-sm)',
                  overflow: 'hidden',
                  opacity: isActive && !isExpired ? 1 : 0.7,
                  animationDelay: `${idx * 60}ms`,
                }}
              >
                {/* Coupon header */}
                <div style={{
                  padding: '16px 20px',
                  background: isActive && !isExpired ? 'var(--primary-light)' : 'var(--bg)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: 1, fontFamily: 'monospace' }}>
                      {coupon.code}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {discountType === 'percent' ? `${discountValue}% 할인` : `${formatWon(discountValue)} 할인`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isExpired && <span className="badge badge-cancelled">만료됨</span>}
                    <span className={`badge ${isActive && !isExpired ? 'badge-success' : 'badge-cancelled'}`}>
                      {isActive && !isExpired ? '활성' : isExpired ? '만료' : '비활성'}
                    </span>
                  </div>
                </div>

                {/* Coupon body */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.82rem' }}>
                    <div>
                      <div style={{ color: 'var(--text-tertiary)', marginBottom: 2 }}>최소 주문금액</div>
                      <div style={{ fontWeight: 600 }}>{minOrder > 0 ? formatWon(minOrder) : '제한 없음'}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-tertiary)', marginBottom: 2 }}>사용현황</div>
                      <div style={{ fontWeight: 600 }}>
                        {usedCount} / {maxUses > 0 ? maxUses : '무제한'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-tertiary)', marginBottom: 2 }}>만료일</div>
                      <div style={{ fontWeight: 600 }}>{expiresAt ? new Date(expiresAt).toLocaleDateString('ko-KR') : '무기한'}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-tertiary)', marginBottom: 2 }}>할인 유형</div>
                      <div style={{ fontWeight: 600 }}>{discountType === 'percent' ? '퍼센트' : '금액'}</div>
                    </div>
                  </div>

                  {/* Usage progress */}
                  {maxUses > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ background: 'var(--bg)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                        <div className="progress-fill" style={{ width: `${Math.min((usedCount / maxUses) * 100, 100)}%`, height: '100%', background: usedCount >= maxUses ? 'var(--danger)' : 'var(--primary)', borderRadius: 4 }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
                  <button
                    className={`btn btn-sm ${isActive ? 'btn-outline' : 'btn-accent'}`}
                    onClick={() => handleToggleActive(coupon)}
                    style={{ flex: 1 }}
                  >
                    {isActive ? '비활성화' : '활성화'}
                  </button>
                  <button className="btn btn-sm btn-outline" onClick={() => openEdit(coupon)}>
                    수정
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => handleDelete(coupon)} style={{ color: 'var(--danger)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6"/>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingCoupon ? '쿠폰 수정' : '쿠폰 생성'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">쿠폰 코드 *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="예: WELCOME10"
                  value={form.code}
                  onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  style={{ fontFamily: 'monospace', letterSpacing: 1 }}
                />
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">할인 유형</label>
                  <select
                    className="form-select"
                    value={form.discountType}
                    onChange={(e) => setForm((p) => ({ ...p, discountType: e.target.value }))}
                  >
                    <option value="percent">퍼센트 (%)</option>
                    <option value="amount">금액 (원)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">할인 값 *</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={form.discountType === 'percent' ? '예: 10' : '예: 5000'}
                    value={form.discountValue}
                    onChange={(e) => setForm((p) => ({ ...p, discountValue: e.target.value }))}
                    min={0}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">최소 주문금액</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0 (제한없음)"
                    value={form.minOrderAmount}
                    onChange={(e) => setForm((p) => ({ ...p, minOrderAmount: e.target.value }))}
                    min={0}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">최대 사용 횟수</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="0 (무제한)"
                    value={form.maxUses}
                    onChange={(e) => setForm((p) => ({ ...p, maxUses: e.target.value }))}
                    min={0}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">만료일</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.expiresAt}
                  onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <><span className="spinner spinner-sm" /> 저장 중...</> : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDialog(null); }}>
          <div className="modal" style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3 className="modal-title">{confirmDialog.title}</h3>
              <button className="modal-close" onClick={() => setConfirmDialog(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {confirmDialog.message}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDialog(null)}>취소</button>
              <button
                className={`btn ${confirmDialog.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmDialog.onConfirm}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponPage;
